(() => {
  if (customElements.get('qr-pack-gallery')) return;
  class QRPackGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.queued) return;
      this.queued = true;
      queueMicrotask(() => {
        this.queued = false;
        if (!this.isConnected || this.ready) return;
        this.slides = [...this.querySelectorAll('[data-qrpack-slide]')];
        this.captions = [...this.querySelectorAll('[data-qrpack-caption]')];
        if (this.slides.length !== 2 || this.captions.length !== 2) return;
        this.ready = true;
        this.abort = new AbortController();
        const signal = this.abort.signal;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.toggle = this.querySelector('[data-qrpack-toggle]');
        this.paused = this.paused || false;
        this.focused = this.contains(document.activeElement);
        this.hovered = false;
        this.visible = false;
        this.show(Number(this.dataset.activeIndex) || 0, false);
        const manual = index => { this.paused = true; this.show(index); this.refresh(); };
        this.querySelector('[data-qrpack-previous]').addEventListener('click', () => manual(this.activeIndex - 1), {signal});
        this.querySelector('[data-qrpack-next]').addEventListener('click', () => manual(this.activeIndex + 1), {signal});
        this.toggle.addEventListener('click', () => { this.paused = !this.paused; this.refresh(); }, {signal});
        this.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.hovered = true; this.refresh(); } }, {signal});
        this.addEventListener('pointerleave', () => { this.hovered = false; this.refresh(); }, {signal});
        this.addEventListener('focusin', () => { this.focused = true; this.refresh(); }, {signal});
        this.addEventListener('focusout', () => queueMicrotask(() => { if (this.ready) { this.focused = this.contains(document.activeElement); this.refresh(); } }), {signal});
        this.motion.addEventListener('change', () => this.refresh(), {signal});
        document.addEventListener('visibilitychange', () => this.refresh(), {signal});
        this.addEventListener('keydown', event => {
          if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
          const offsets = {ArrowLeft:-1, ArrowRight:1};
          if (!(event.key in offsets) && event.key !== 'Home' && event.key !== 'End') return;
          event.preventDefault();
          manual(event.key === 'Home' ? 0 : event.key === 'End' ? 1 : this.activeIndex + offsets[event.key]);
        }, {signal});
        const frame = this.querySelector('[data-qrpack-frame]');
        frame.addEventListener('pointerdown', event => {
          this.paused = true; this.refresh();
          if (event.pointerType !== 'touch' || !event.isPrimary) return;
          this.touch = {id:event.pointerId, x:event.clientX, y:event.clientY, time:performance.now()};
        }, {signal, passive:true});
        window.addEventListener('pointerup', event => {
          const touch = this.touch;
          if (!touch || event.pointerId !== touch.id) return;
          this.touch = null;
          const dx = event.clientX - touch.x, dy = event.clientY - touch.y;
          if (Math.abs(dx) < 55 || Math.abs(dx) < Math.abs(dy) * 1.4 || performance.now() - touch.time > 900) return;
          this.ignoreClickUntil = performance.now() + 500;
          manual(this.activeIndex + (dx < 0 ? 1 : -1));
        }, {signal, passive:true});
        window.addEventListener('pointercancel', () => { this.touch = null; }, {signal, passive:true});
        frame.addEventListener('click', event => {
          this.paused = true; this.refresh();
          if (performance.now() < (this.ignoreClickUntil || 0)) { event.preventDefault(); event.stopImmediatePropagation(); }
        }, {signal, capture:true});
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.refresh(); }, {threshold:0.15});
        this.observer.observe(frame);
        this.refresh();
      });
    }
    refresh() {
      clearTimeout(this.timer);
      if (!this.ready) return;
      this.toggle.hidden = this.motion.matches;
      const label = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.querySelector('[data-qrpack-motion-label]').textContent = label;
      this.playing = !this.paused && !this.motion.matches && !this.hovered && !this.focused && this.visible && !document.hidden;
      if (this.playing) this.timer = setTimeout(() => { this.show(this.activeIndex + 1, false); this.refresh(); }, 5000);
    }
    show(index, announce = true) {
      const mediaFocused = this.slides[this.activeIndex]?.contains(document.activeElement);
      this.activeIndex = (index + this.slides.length) % this.slides.length;
      this.dataset.activeIndex = this.activeIndex;
      this.slides.forEach((slide, i) => {
        const inactive = i !== this.activeIndex;
        slide.setAttribute('aria-hidden', String(inactive)); slide.inert = inactive;
        this.captions[i].setAttribute('aria-hidden', String(inactive)); this.captions[i].inert = inactive;
      });
      const counter = this.dataset.counterTemplate.replace('__INDEX__', this.activeIndex + 1);
      this.querySelector('[data-qrpack-counter]').textContent = counter;
      if (announce) this.querySelector('[data-qrpack-status]').textContent = this.captions[this.activeIndex].querySelector('strong').textContent + ', ' + counter;
      if (mediaFocused) this.slides[this.activeIndex].querySelector('a').focus({preventScroll:true});
    }
    disconnectedCallback() {
      clearTimeout(this.timer); this.observer?.disconnect(); this.abort?.abort();
      this.touch = null; this.ready = false; this.queued = false;
    }
  }
  customElements.define('qr-pack-gallery', QRPackGallery);
})();
